import { DateTime } from '@ksyos/date-time';
import {
    Alert,
    Box,
    Button,
    Card,
    Col,
    Form,
    FormButtonRow,
    FormSection,
    IconButton,
    Row,
    Text,
} from '@ksyos/design-system';
import { groupBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { ActivityType, EpisodeCategory, ServiceCode, ServiceType } from '@common/enums';
import type { ICompensationPayout } from '@frontend/components/interfaces/compensationPayout';
import { GgzProfession, ManagerAuthorization } from '@common/enums';
import { doesPayoutDependOnProfession } from '@common/utils/doesPayoutDependOnProfession';
import { formatString } from '@common/utils/formatString';
import { getValidationSchema } from '@common/validation/compensationPayout';
import { validateDatetimeOverlap } from '@common/validation/utils/validateDatetimeOverlap';
import { Currency, DateInput, EnumDropdown, InputCurrency } from '@frontend/components/elements/common';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { getSelectedLanguage } from '@frontend/translate';
import {
    emptyValidationErrors,
    hasOneAuthorization,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
} from '@frontend/utils';
import type { ICompensationPayoutWithErrors } from './helpers';
import { isConfirmationRequired, showPayoutOnProfessionAlert, validateDateBoundary } from './helpers';
import { getBadge, getCompensationEndDate, getOverlappingMessage } from './sharedComponents';

interface IActivityCompensationPayoutsAddEditProps {
    activityType: ActivityType;
    compensationPayouts: ICompensationPayout[];
    endDate?: string | Date;
    episodeCategory: EpisodeCategory;
    higherLevelCompensationPayout?: ICompensationPayout;
    isOnOrganizationLevel: boolean;
    onSubmit: () => void;
    organizationId: string;
    organizationLevelPayoutsStartDate?: string;
    serviceCode?: ServiceCode;
    serviceConsumerId?: string;
    serviceOrganizationSettingId?: string;
    serviceProviderId?: string;
    serviceSupervisorId?: string;
    serviceType?: ServiceType;
    startDate?: string | Date;
    title: string;
}

export function ActivityCompensationPayoutsAddEdit(props: IActivityCompensationPayoutsAddEditProps) {
    const abortableFetch = useAbortableFetch();
    const language = getSelectedLanguage();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

    const isUpdatingEmployment = props.serviceConsumerId || props.serviceProviderId || props.serviceSupervisorId;
    const canEdit = isUpdatingEmployment
        ? hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit)
        : hasOneAuthorization(ManagerAuthorization.organizationAddEdit);

    const [compensations, setCompensations] = useState<ICompensationPayoutWithErrors[]>([]);
    const {
        compensationPayouts,
        higherLevelCompensationPayout,
        title,
        activityType,
        startDate,
        endDate,
        serviceSupervisorId,
        serviceOrganizationSettingId,
        serviceProviderId,
        serviceConsumerId,
        organizationId,
        episodeCategory,
        onSubmit,
        serviceCode,
        serviceType,
        isOnOrganizationLevel,
        organizationLevelPayoutsStartDate,
    } = props;

    const isGgzNewContract = serviceCode
        ? doesPayoutDependOnProfession(serviceCode, activityType) && isOnOrganizationLevel
        : false;

    let payoutPerProfessionAlert;

    if (
        organizationLevelPayoutsStartDate &&
        showPayoutOnProfessionAlert(organizationLevelPayoutsStartDate, serviceCode, activityType)
    ) {
        payoutPerProfessionAlert = isOnOrganizationLevel
            ? formatString(language.compensationPayouts.organizationPayoutPerProfession, [
                  DateTime.from(organizationLevelPayoutsStartDate).format('DD-MM-YYYY'),
              ])
            : formatString(language.compensationPayouts.ignoredFeesPractitionerDueToProfessionLevelPayout, [
                  DateTime.from(organizationLevelPayoutsStartDate).format('DD-MM-YYYY'),
              ]);
    }

    useEffect(() => {
        const compensationsWithErrors = compensationPayouts.map((item: ICompensationPayout) => ({
            ...item,
            errors: emptyValidationErrors,
        }));
        setCompensations(compensationsWithErrors);
    }, [compensationPayouts]);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            {canEdit ? (
                <Card.Header
                    action={
                        !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={title}
                />
            ) : (
                <Card.Header title={title} />
            )}
            <Card.Body>
                <Form submitOnEnter={false}>
                    {payoutPerProfessionAlert ? (
                        <Alert appearance="info">{payoutPerProfessionAlert}</Alert>
                    ) : (
                        getOverlappingMessage(higherLevelCompensationPayout, getLink())
                    )}
                    <FormSection>
                        <Row
                            columnGap={4}
                            rowGap={4}
                        >
                            <Col span={[12, 2]}>{language.compensationPayouts.fees}</Col>
                            {isGgzNewContract && <Col span={[12, 3]}>{language.common.profession}</Col>}
                            <Col span={[12, 7]}>{language.common.startDateAndEndDate} </Col>
                        </Row>
                    </FormSection>

                    {compensations.map((compensationPayout, key) => {
                        return (
                            <FormSection key={key}>
                                <Row
                                    columnGap={4}
                                    rowGap={4}
                                >
                                    {!isEditing && (
                                        <>
                                            <Col span={[12, 2]}>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    <Currency value={compensationPayout.amount} />
                                                </Text>
                                            </Col>
                                            {isGgzNewContract && (
                                                <Col span={[12, 3]}>
                                                    {compensationPayout.profession && (
                                                        <Text
                                                            as="label"
                                                            fontWeight="medium"
                                                        >
                                                            {language.professionEnum[compensationPayout.profession]}
                                                        </Text>
                                                    )}
                                                </Col>
                                            )}
                                            <Col span={[12, 5]}>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    <NullableFormattedDate value={compensationPayout.startDate} />
                                                </Text>
                                                <Text
                                                    as="label"
                                                    mx={4}
                                                    size="md"
                                                >
                                                    {language.common.until}
                                                </Text>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    {getCompensationEndDate(compensationPayout.endDate, endDate)}
                                                </Text>
                                            </Col>
                                            <Col span={[12, 2]}>
                                                {getBadge({
                                                    activityType,
                                                    compensationPayout: compensationPayout as ICompensationPayout,
                                                    higherLevelCompensationPayout,
                                                    isOnOrganizationLevel,
                                                    organizationLevelPayoutsStartDate,
                                                    profession: compensationPayout.profession,
                                                    rightEndDate: endDate,
                                                    rightStartDate: startDate,
                                                    serviceCode,
                                                })}
                                            </Col>
                                        </>
                                    )}

                                    {isEditing && (
                                        <>
                                            <Col span={[12, 2]}>
                                                <Box display="flex">
                                                    <Text
                                                        as="label"
                                                        mr={2.5}
                                                        pt={2.5}
                                                        size="xs"
                                                    >
                                                        €
                                                    </Text>
                                                    <InputCurrency
                                                        error={compensationPayout.errors.get('amount')}
                                                        onChange={(value) => handleChange('amount', value, key)}
                                                        onDoValidate={() => {
                                                            return;
                                                        }}
                                                        value={compensationPayout.amount}
                                                    />
                                                </Box>
                                            </Col>
                                            {isGgzNewContract && (
                                                <Col span={[12, 3]}>
                                                    <Box display="flex">
                                                        <EnumDropdown
                                                            enumName="profession"
                                                            error={compensationPayout.errors.get('profession')}
                                                            jsEnum={GgzProfession}
                                                            onChange={(value) => handleChange('profession', value, key)}
                                                            sortDirection="ASC"
                                                            value={compensationPayout.profession ?? null}
                                                        />
                                                    </Box>
                                                </Col>
                                            )}
                                            <Col span={[12, 6]}>
                                                <Box
                                                    display="flex"
                                                    flexDirection={['column', 'row']}
                                                    gap={2}
                                                >
                                                    <Box>
                                                        <DateInput
                                                            error={compensationPayout.errors.get('startDate')}
                                                            maxDate={endDate as string}
                                                            minDate={startDate as string}
                                                            onChange={(value) => handleChange('startDate', value, key)}
                                                            placeholder={language.common.dateFormat}
                                                            showOutOfBoundErrorsValidation={true}
                                                            value={compensationPayout.startDate}
                                                            variant="outline"
                                                        />
                                                    </Box>
                                                    <Text
                                                        as="label"
                                                        pt={2.5}
                                                        size="xs"
                                                    >
                                                        {language.common.until}
                                                    </Text>
                                                    <Box>
                                                        <DateInput
                                                            error={compensationPayout.errors.get('endDate')}
                                                            maxDate={endDate as string}
                                                            minDate={startDate as string}
                                                            onChange={(value) => handleChange('endDate', value, key)}
                                                            placeholder={language.common.dateFormat}
                                                            showOutOfBoundErrorsValidation={true}
                                                            value={compensationPayout.endDate}
                                                            variant="outline"
                                                        />
                                                    </Box>
                                                </Box>
                                            </Col>
                                            <Col span={[12, 1]}>
                                                <Box
                                                    display="flex"
                                                    justifyContent={['center', 'flex-end']}
                                                >
                                                    <IconButton
                                                        icon="delete"
                                                        onClick={() => onRemoveCompensation(key)}
                                                    />
                                                </Box>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </FormSection>
                        );
                    })}

                    {isEditing && (
                        <Button
                            onClick={() => onAddCompensation()}
                            style={{ marginTop: '32px' }}
                            variant="link"
                        >
                            {language.compensationPayouts.addFees}
                        </Button>
                    )}

                    {isEditing && (
                        <FormButtonRow>
                            <CancelButton
                                disabled={isSaving}
                                onCancel={async () => handleCancel()}
                            />
                            <SaveButton
                                disabled={isSaving}
                                onSave={async () => {
                                    if (hasValidationErrors()) {
                                        return;
                                    }
                                    isConfirmationRequired(compensations, compensationPayouts)
                                        ? setShowConfirmationModal(true)
                                        : await handleSubmit();
                                }}
                            />
                        </FormButtonRow>
                    )}

                    <ConfirmationModal
                        cancelText={language.common.cancelling}
                        confirmText={language.common.proceed}
                        handleNo={() => setShowConfirmationModal(false)}
                        handleYes={() => {
                            setShowConfirmationModal(false);
                            handleSubmit();
                        }}
                        isOpen={showConfirmationModal}
                        question="Deze wijziging heeft financiële gevolgen op reeds uitbetaalde processen. Neem contact op met Finance."
                        title="Financiële gevolgen ingestelde datum"
                    />
                </Form>
            </Card.Body>
        </Card>
    );

    function handleCancel() {
        setCompensations(
            compensationPayouts.map((item: ICompensationPayout) => ({ ...item, errors: emptyValidationErrors }))
        );
        setIsSaving(false);
        setIsEditing(false);
    }

    async function handleSubmit() {
        setIsSaving(true);

        const hasErrors = hasValidationErrors();

        if (!hasErrors) {
            const updatedCompensationPayouts = compensations.map((compensation) => {
                return {
                    compensationPayout: {
                        amount: compensation.amount,
                        endDate: compensation.endDate,
                        profession: compensation.profession,
                        startDate: compensation.startDate,
                        type: compensation.type,
                    },
                    id: compensation.id,
                };
            });

            await abortableFetch.fetchJson('/api/compensationPayouts/updateAll', 'POST', {
                activityType,
                compensationPayouts: updatedCompensationPayouts,
                serviceConsumerId,
                serviceOrganizationSettingId,
                serviceProviderId,
                serviceSupervisorId,
            });

            setIsEditing(false);
            onSubmit();
        }
        setIsSaving(false);
    }

    function handleChange<K extends keyof ICompensationPayoutWithErrors>(
        name: K,
        value: ICompensationPayoutWithErrors[K],
        index: number
    ) {
        const updateCompensations = [...compensations];
        const compensationToUpdate = { ...compensations[index] };
        compensationToUpdate[name] = value;

        const schema = getValidationSchema(compensationToUpdate.id);
        if (name === 'startDate') {
            compensationToUpdate.errors = updateValidationErrorsForMany(
                schema,
                compensationToUpdate,
                compensationToUpdate.errors,
                ['startDate', 'endDate']
            );
        } else {
            compensationToUpdate.errors = updateValidationErrorsForOne(
                schema,
                compensationToUpdate,
                compensationToUpdate.errors,
                name
            );
        }

        const { errors } = validateDateBoundary(compensationToUpdate, startDate, endDate);
        compensationToUpdate.errors = errors;

        updateCompensations[index] = compensationToUpdate;
        setCompensations(updateCompensations);
    }

    function onRemoveCompensation(index: number) {
        const updateCompensations = [...compensations];
        updateCompensations.splice(index, 1);
        setCompensations(updateCompensations);
    }

    function createNewCompensation(): ICompensationPayoutWithErrors {
        return {
            amount: null,
            endDate: null,
            errors: emptyValidationErrors,
            startDate: null,
            type: activityType,
        };
    }

    function onAddCompensation() {
        setCompensations([...compensations, createNewCompensation()]);
    }

    function hasValidationErrors() {
        const validatedCompensations = validateCompensations(compensations);
        const hasErrors = validatedCompensations.some((compensation) =>
            compensation.errors.some((error) => error !== undefined)
        );
        if (hasErrors) {
            setCompensations(validatedCompensations);
        }

        return hasErrors;
    }

    function validateCompensations(items: ICompensationPayoutWithErrors[]) {
        const compensationsToValidate = [...items];
        let hasErrors = false;

        for (const compensation of compensationsToValidate) {
            compensation.errors = emptyValidationErrors;

            const validationResult = validateCompensation(compensation);
            if (validationResult.hasErrors) {
                hasErrors = true;
                compensation.errors = validationResult.errors;
            }
            const dateBoundaryValidationResult = validateDateBoundary(compensation, startDate, endDate);
            if (dateBoundaryValidationResult.hasErrors) {
                hasErrors = true;
                compensation.errors = dateBoundaryValidationResult.errors;
            }
        }

        if (!hasErrors) {
            const compensationsPerProfession = groupBy(compensationsToValidate, (item) => item.profession ?? 'unknown');

            return Object.values(compensationsPerProfession).flatMap(validateDatetimeOverlap);
        }

        return compensationsToValidate;
    }

    function validateCompensation(compensation: ICompensationPayoutWithErrors) {
        const schema = getValidationSchema(compensation.id);
        const compensationValidationResult = validate(schema, compensation);

        return compensationValidationResult;
    }

    function getLink() {
        if (!higherLevelCompensationPayout) {
            return;
        }

        let link = `/compensationPayouts/${higherLevelCompensationPayout.id}?serviceType=${serviceType}&serviceCode=${serviceCode}`;
        if (higherLevelCompensationPayout.serviceGroupSettingId) {
            link += `&serviceGroupSettingId=${higherLevelCompensationPayout.serviceGroupSettingId}`;
        } else {
            link = `/organizations/${organizationId}/view/compensationPayouts/${episodeCategory}`;
        }

        return link;
    }
}
